
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UseFormReturn } from "react-hook-form";
import { UnitField } from "./UnitField";
import { BaseProductFormValues } from "@/types/stock";
import { HelpCircle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface BaseProductFieldsProps {
  form: UseFormReturn<BaseProductFormValues>;
  isPoultry?: boolean;
}

export const BaseProductFields = ({
  form,
  isPoultry = false,
}: BaseProductFieldsProps) => {
  const { register, watch, setValue } = form;
  const unit = watch("unit");
  const customUnit = watch("customUnit");
  const price = watch("price");
  const purchasePrice = watch("purchase_price");

  // Calculer la marge en temps réel
  const margin = price && purchasePrice 
    ? ((Number(price) - Number(purchasePrice)) / Number(price) * 100).toFixed(2)
    : "0";

  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="name">
          {isPoultry ? "Race/Type de volaille" : "Nom du produit"}
        </Label>
        <Input
          id="name"
          {...register("name")}
          required
          placeholder={isPoultry ? "Ex: Poulet de chair" : "Nom du produit"}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="quantity">Quantité</Label>
          <Input
            id="quantity"
            type="number"
            min="0"
            {...register("quantity")}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="minQuantity">Quantité minimum</Label>
          <Input
            id="minQuantity"
            type="number"
            min="0"
            {...register("minQuantity")}
            required
          />
        </div>
      </div>

      <UnitField
        unit={unit}
        customUnit={customUnit}
        onUnitChange={(value) => setValue("unit", value)}
        onCustomUnitChange={(value) => setValue("customUnit", value)}
        isPoultry={isPoultry}
      />

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="purchase_price">Prix d'achat</Label>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-gray-500" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Prix auquel vous achetez le produit (optionnel)</p>
              </TooltipContent>
            </Tooltip>
          </div>
          <Input
            id="purchase_price"
            type="number"
            min="0"
            {...register("purchase_price")}
            placeholder="Prix d'achat unitaire (optionnel)"
          />
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="price">Prix de vente</Label>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-gray-500" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Prix auquel vous vendez le produit</p>
              </TooltipContent>
            </Tooltip>
          </div>
          <Input
            id="price"
            type="number"
            min="0"
            {...register("price")}
            required
            placeholder="Prix de vente unitaire"
          />
        </div>
      </div>

      {price && purchasePrice && (
        <div className="text-sm text-gray-600">
          Marge brute: {margin}%
          {Number(margin) < 0 && (
            <span className="text-red-500 ml-2">
              ⚠️ Attention : prix de vente inférieur au prix d'achat
            </span>
          )}
        </div>
      )}
    </>
  );
};
